/**
 * Provides an administrative interface for configuring Analytics Insights
 * widget settings. Allows administrators to manage the Power BI integration
 * endpoint used by the Analytics Insights dashboard, update reporting URLs,
 * review pending configuration changes, and persist analytics-related
 * settings that control access to embedded business intelligence content.
 */
import React, { useEffect, useState } from "react";
import {
  getWidgetConfigurations,
  updateWidgetConfigurations,
} from "@/services/widgetConfig";

const FEATURE_CODE = "dashboard.analyticsinsights";

const AnalyticsConfig: React.FC = () => {
  const [savedConfig, setSavedConfig] = useState<any>(null);
  const [saving, setSaving] = useState(false);

  const [analyticsPowerBiUrl, setAnalyticsPowerBiUrl] = useState("");

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    const config = await getWidgetConfigurations();

    const analyticsConfig = config?.[FEATURE_CODE] || {};

    setSavedConfig(analyticsConfig);

    setAnalyticsPowerBiUrl(
      analyticsConfig.PowerBIAnalyticsUrl || "",
    );
  };

  const hasChanges =
    !!savedConfig &&
    analyticsPowerBiUrl !==
      (savedConfig.PowerBIAnalyticsUrl || "");

  const handleSave = async () => {
    setSaving(true);

    try {
      const payload = {
        featureCode: FEATURE_CODE,
        configs: {
          PowerBIAnalyticsUrl: analyticsPowerBiUrl,
        },
      };

      await updateWidgetConfigurations(payload);

      setSavedConfig(payload.configs);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (!savedConfig) return;

    setAnalyticsPowerBiUrl(
      savedConfig.PowerBIAnalyticsUrl || "",
    );
  };

  return (
    <div className="border border-gray-200 rounded-md bg-[#fbfaf7] p-3 space-y-3">
      <div className="flex items-center gap-4 text-xs">
        <span className="w-44 text-gray-700">
          Analytics Power BI URL
        </span>

        <input
          type="text"
          value={analyticsPowerBiUrl}
          onChange={(e) => setAnalyticsPowerBiUrl(e.target.value)}
          className="flex-1 border border-gray-300 rounded px-3 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-[var(--color-brand)]"
          placeholder="Enter Power BI URL"
        />
      </div>

      <div className="flex justify-end gap-2 items-center pt-2">
        {hasChanges && (
          <div className="text-orange-600 border border-orange-300 bg-orange-50 px-2 py-0.5 rounded text-xs">
            Unsaved
          </div>
        )}

        <button
          className="bg-[#e7e5e0] px-3 py-1 text-xs"
          onClick={handleCancel}
          disabled={!hasChanges}
        >
          Cancel
        </button>

        <button
          className="bg-[var(--color-brand)] text-white px-3 py-1 text-xs"
          onClick={handleSave}
          disabled={!hasChanges || saving}
        >
          {saving ? "Saving..." : "Save"}
        </button>
      </div>
    </div>
  );
};

export default AnalyticsConfig;